import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { fetchQuery, graphql } from "react-relay"
import { uniqBy } from "es-toolkit/array"
import { debounce } from "es-toolkit/function"
import { Input } from "@/components/ui/input"
import { citySelectorGetCitiesQuery } from "@/modules/inbound-wizard/components/contact/address/__generated__/citySelectorGetCitiesQuery.graphql"
import {
  SelectBoxOption,
  SelectBoxWithSearch,
} from "@/modules/inbound-wizard/components/contact/address/selectbox-with-search"
import { RelayEnvironment } from "@/relay-environment"

export type CityData = SelectBoxOption & { postalCode?: string }

export type CitySelectorValueProps = {
  value: string
  onChange?: (value: string | undefined) => void
  error?: string | undefined
  onSelect: (value?: CityData) => void
  ref?: React.Ref<HTMLInputElement>
  id?: string
  className?: React.HTMLAttributes<HTMLDivElement>["className"]
  required?: boolean
}

export type CitySelectorProps = CitySelectorValueProps & {
  city?: CityData
  countryIsoCode: string
}

export const MINIMUM_CITY_SEARCH_KEYWORD_LENGTH = 3

export const shouldTriggerCitySearch = (keyword: string) => {
  const trimmedKeyword = keyword.trim()
  return trimmedKeyword.length === 0 || trimmedKeyword.length >= MINIMUM_CITY_SEARCH_KEYWORD_LENGTH
}

export const CitySelector = (props: CitySelectorProps) => {
  const { city, countryIsoCode, value, onChange, error, onSelect, ref, id, required } = props
  const { t } = useTranslation()
  const testId = "patient-city"

  const [cities, setCities] = useState<ReadonlyArray<CityData>>([])

  const handleSearchCity = useCallback(async (keyword: string, countryIsoCode: string) => {
    const response = await fetchQuery<citySelectorGetCitiesQuery>(
      RelayEnvironment,
      graphql`
        query citySelectorGetCitiesQuery($countryIsoCode: String!, $keyword: String) {
          displayableCities(countryIsoCode: $countryIsoCode, keyword: $keyword) {
            label
            value
            postalCode
          }
        }
      `,
      { countryIsoCode, keyword },
      { fetchPolicy: "store-or-network" },
    ).toPromise()

    setCities((previous) => response?.displayableCities ?? previous)
  }, [])

  const debounceSearchCity = useRef(
    debounce((keyword: string, countryIsoCode: string) => {
      if (!shouldTriggerCitySearch(keyword)) return
      handleSearchCity(keyword, countryIsoCode)
    }, 250),
  ).current
  const isSwitzerlandSelected = countryIsoCode === "CH"

  useEffect(() => {
    if (isSwitzerlandSelected) {
      handleSearchCity("", countryIsoCode)
    } else {
      setCities([])
    }
  }, [countryIsoCode, handleSearchCity, isSwitzerlandSelected])

  const mergedOptions = city ? uniqBy(cities.concat(city), (item) => item.value) : cities

  useEffect(() => {
    if (countryIsoCode) {
      debounceSearchCity(value, countryIsoCode)
    }
  }, [value, countryIsoCode, debounceSearchCity])

  const errorMsg = t(error ?? "")
  if (!isSwitzerlandSelected)
    return (
      <Input
        ref={ref}
        wrapperProps={{ className: props.className }}
        label={t("common.city")}
        required={required}
        error={errorMsg}
        onChange={(e) => onChange?.(e.target.value)}
        value={value}
        id={id}
        testId={testId}
      />
    )

  return (
    <SelectBoxWithSearch
      ref={ref}
      className={props.className}
      type={id ?? "city"}
      options={mergedOptions}
      value={value ?? ""}
      placeholder={t("common.city")}
      required={required}
      error={errorMsg}
      testId={testId}
      onChange={(option?: CityData) => {
        onChange?.(option?.value)
        onSelect(option)
      }}
      onSearchChange={(val) => {
        onChange?.(val)
        debounceSearchCity(val, countryIsoCode)
      }}
      renderOption={(option: CityData) => (
        <div className="grid grid-cols-1 py-0.5">
          <div className="text-body-sm text-on-surface block truncate">{option.label}</div>
          <div className="text-body-sm order-1 block truncate">{option.postalCode}</div>
        </div>
      )}
      ignoreLocalFilter
    />
  )
}
